import { useCallback, useEffect, useState } from "react";
import { getNewsroomCategories, getNewsroomPosts } from "../../../../services/StudentPortal/StudentNewsroomService";
import { handleApiError } from "../../../../utils/errorHandler";
import { unwrapList } from "../../utils/studentPortalUtils";

/**
 * Loads the Newsroom categories once on mount, selects the first one by
 * default, then (re)loads the posts for whichever category is active.
 * Confirmed (2026-10-22): neither call needs student_id/program_id/session_id
 * — categories come from our own backend (auth only) and posts come straight
 * from the public apeejay.news WordPress REST API.
 */
export const useStudentNewsroom = () => {
  const [categories, setCategories] = useState([]);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchCategories = async () => {
      setIsLoadingCategories(true);
      try {
        const response = await getNewsroomCategories();
        const list = unwrapList(response);
        if (!isMounted) return;
        setCategories(list);
        if (list.length > 0) {
          setActiveCategoryId(list[0]?.id ?? null);
        }
      } catch (error) {
        handleApiError(error);
      } finally {
        if (isMounted) setIsLoadingCategories(false);
      }
    };

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  const fetchPosts = useCallback(async () => {
    if (!activeCategoryId) return;
    setIsLoadingPosts(true);
    try {
      const response = await getNewsroomPosts(activeCategoryId);
      // Unlike every other StudentPortal call, this hits the public
      // apeejay.news WordPress API directly via plain axios — its
      // response.data IS the posts array itself, not wrapped in the
      // {data: {data: [...]}} envelope our own backend uses, so the shared
      // unwrapList (built for that envelope) would always resolve to [].
      setPosts(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoadingPosts(false);
    }
  }, [activeCategoryId]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  return {
    categories,
    activeCategoryId,
    setActiveCategoryId,
    posts,
    isLoadingCategories,
    isLoadingPosts,
  };
};
